import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { TopNav } from './TopNav';
import { Footer } from './Footer';
import { ImpersonationBanner } from './ImpersonationBanner';
import { SiteBanner } from './SiteBanner';

/**
 * True once the window has scrolled past `threshold` px. Pass
 * `enabled=false` to skip the scroll listener entirely on routes
 * that don't react to scroll position.
 */
function useScrolled(threshold = 24, enabled = true) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (!enabled) {
      if (scrolled) setScrolled(false);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threshold, enabled]);
  return scrolled;
}

/**
 * Public site shell: top nav + page outlet + footer.
 *
 * The nav stays pinned to the top on every route. On routes that open with a
 * full-bleed brand-blue hero (`/`, `/pricing.html`) it overlays the hero —
 * transparent at the top, then switching to the solid sky-blue bar once
 * scrolled so the links stay legible over page content. On every other route
 * it's the solid sky-blue bar sitting in the document flow.
 */
const OVERLAY_HERO_ROUTES = new Set(['/', '/pricing.html']);

export function DefaultLayout() {
  const { pathname } = useLocation();
  const overlayHero = OVERLAY_HERO_ROUTES.has(pathname);
  // Only attach the scroll listener on routes that actually swap the
  // TopNav variant on scroll. Otherwise the hook fires on every public
  // route (Blogs / News / Pricing / etc) for no rendered effect.
  const scrolled = useScrolled(24, overlayHero);

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      {/* Yellow banner sits ABOVE every layout chrome — including the
          overlay-hero TopNav variant. Renders nothing when no admin
          impersonation session is active. */}
      <ImpersonationBanner />
      <SiteBanner />
      {overlayHero ? (
        <div className="fixed inset-x-0 top-0 z-40">
          <TopNav variant={scrolled ? 'brand' : 'transparent'} />
        </div>
      ) : (
        <TopNav variant="brand" />
      )}
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
